import { useCallback, useEffect, useState } from "react";
import { lumiFetch } from "./lumiApi";
import { toast } from "./lumiToast";

// Louise's "Organisation Subscribers" panel. Reuses the existing Lumi
// contacts/unsubscribe/suppression system — new backend collection, same
// HMAC unsubscribe URL. Displays the full lifecycle (verification →
// purchase → marketing status) with filters + admin-manual override.

const SECTOR_LABEL = { hotel: "Hotel", school: "School", charity: "Charity/Org" };
const MARKETING_LABEL = {
  subscribed: "Subscribed",
  not_subscribed: "Not subscribed",
  unsubscribed: "Unsubscribed",
  consent_unknown: "Consent unknown",
};
const MARKETING_CLASS = {
  subscribed:      "bg-green-500/20 text-green-300 border-green-400/30",
  not_subscribed:  "bg-white/10 text-white/70 border-white/20",
  unsubscribed:    "bg-red-500/20 text-red-300 border-red-400/30",
  consent_unknown: "bg-yellow-500/20 text-yellow-300 border-yellow-400/30",
};

export default function OrgSubscribersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sector, setSector] = useState("");
  const [marketing, setMarketing] = useState("");
  const [membership, setMembership] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const qs = new URLSearchParams();
      if (sector) qs.set("sector", sector);
      if (marketing) qs.set("marketing", marketing);
      if (membership) qs.set("membership", membership);
      if (q) qs.set("q", q);
      const r = await lumiFetch(`/org-subscribers${qs.toString() ? `?${qs}` : ""}`);
      setRows(r.subscribers || []);
    } catch (e) {
      setErr(e?.data?.detail || "Could not load subscribers");
    } finally { setLoading(false); }
  }, [sector, marketing, membership, q]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (sub_id, newStatus) => {
    try {
      const note = prompt(`Reason for setting marketing status to "${newStatus}"? (Optional)`, "");
      if (note === null) return;
      await lumiFetch(`/org-subscribers/marketing-status`, {
        method: "POST",
        body: JSON.stringify({ sub_id, marketing_status: newStatus, note }),
      });
      toast.success(`Marketing status updated to ${newStatus}`);
      await load();
    } catch (e) {
      toast.error(e?.data?.detail || "Update failed");
    }
  };

  const runBackfill = async () => {
    try {
      const r = await lumiFetch(`/org-subscribers/backfill`, { method: "POST" });
      toast.success(`Backfill complete — ${r.from_hotels} hotels, ${r.from_verifications} verifications`);
      await load();
    } catch (e) {
      toast.error(e?.data?.detail || "Backfill failed");
    }
  };

  const selectableCount = rows.filter((r) => r.marketing_status === "subscribed").length;

  const toggleAll = () => {
    if (selected.size === selectableCount) setSelected(new Set());
    else setSelected(new Set(rows.filter((r) => r.marketing_status === "subscribed").map((r) => r.sub_id)));
  };

  const toggleOne = (sub_id, marketing_status) => {
    if (marketing_status !== "subscribed") return; // only subscribed rows are selectable
    const s = new Set(selected);
    if (s.has(sub_id)) s.delete(sub_id); else s.add(sub_id);
    setSelected(s);
  };

  return (
    <div className="text-white" data-testid="org-subscribers-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={sector} onChange={(e) => setSector(e.target.value)} data-testid="orgsubs-filter-sector"
          className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm">
          <option value="">All sectors</option>
          <option value="hotel">Hotels</option>
          <option value="school">Schools</option>
          <option value="charity">Charities / Orgs</option>
        </select>
        <select value={marketing} onChange={(e) => setMarketing(e.target.value)} data-testid="orgsubs-filter-marketing"
          className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm">
          <option value="">Any marketing status</option>
          <option value="subscribed">Subscribed</option>
          <option value="not_subscribed">Not subscribed</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="consent_unknown">Consent unknown</option>
        </select>
        <select value={membership} onChange={(e) => setMembership(e.target.value)} data-testid="orgsubs-filter-membership"
          className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm">
          <option value="">Any purchase state</option>
          <option value="active">Active members</option>
          <option value="inactive">Inactive / cancelled</option>
          <option value="verified_not_purchased">Verified but not purchased</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / contact"
          data-testid="orgsubs-search"
          className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm flex-1 min-w-[180px]" />
        <button onClick={runBackfill} data-testid="orgsubs-backfill"
          className="text-xs px-3 py-1 bg-white/10 border border-white/20 rounded hover:bg-white/20">Re-run backfill</button>
      </div>

      <div className="text-xs text-white/60 mb-2 flex justify-between">
        <span>{rows.length} total · {selectableCount} subscribed (selectable for campaigns)</span>
        <span>
          <button onClick={toggleAll} className="underline">
            {selected.size === selectableCount && selectableCount > 0 ? "Clear selection" : "Select all subscribed"}
          </button>
          <span className="ml-3">{selected.size} selected</span>
        </span>
      </div>

      {err && <div className="bg-red-500/20 border border-red-400/30 text-red-300 rounded p-2 text-sm mb-2">{err}</div>}
      {loading && <p className="text-white/60 text-sm">Loading…</p>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/60 border-b border-white/10">
              <th className="p-2 w-8"></th>
              <th className="p-2">Organisation</th>
              <th className="p-2">Sector</th>
              <th className="p-2">Contact</th>
              <th className="p-2">Verify</th>
              <th className="p-2">Membership</th>
              <th className="p-2">Tier</th>
              <th className="p-2">Balance</th>
              <th className="p-2">Marketing</th>
              <th className="p-2">Consent</th>
              <th className="p-2">Added</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sub_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`orgsubs-row-${r.sub_id}`}>
                <td className="p-2">
                  <input type="checkbox" disabled={r.marketing_status !== "subscribed"}
                    checked={selected.has(r.sub_id)} onChange={() => toggleOne(r.sub_id, r.marketing_status)}
                    data-testid={`orgsubs-select-${r.sub_id}`} />
                </td>
                <td className="p-2 font-semibold">{r.org_name || "—"}</td>
                <td className="p-2">{SECTOR_LABEL[r.sector] || r.sector}</td>
                <td className="p-2">
                  <div>{r.contact_name || "—"}</div>
                  <div className="text-white/50 text-xs">{r.email}</div>
                </td>
                <td className="p-2">{(r.verification_status || "—").replace(/_/g, " ")}</td>
                <td className="p-2">{r.membership_status || "—"}</td>
                <td className="p-2">{r.membership_tier || "—"}</td>
                <td className="p-2">{r.credit_balance ?? "—"}</td>
                <td className="p-2">
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${MARKETING_CLASS[r.marketing_status] || MARKETING_CLASS.consent_unknown}`}>
                    {MARKETING_LABEL[r.marketing_status] || (r.marketing_status || "unknown")}
                  </span>
                </td>
                <td className="p-2 text-xs text-white/60">
                  {r.marketing_consent_source ? (
                    <>
                      <div>{r.marketing_consent_source}</div>
                      {r.marketing_consent_at && <div>{new Date(r.marketing_consent_at).toLocaleString()}</div>}
                    </>
                  ) : "—"}
                </td>
                <td className="p-2 text-xs text-white/60">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-2 text-right">
                  <select
                    defaultValue=""
                    onChange={(e) => { if (e.target.value) { setStatus(r.sub_id, e.target.value); e.target.value = ""; } }}
                    data-testid={`orgsubs-action-${r.sub_id}`}
                    className="bg-white/10 border border-white/20 rounded px-1 py-0.5 text-xs"
                  >
                    <option value="" disabled>Change status…</option>
                    <option value="subscribed">Mark subscribed</option>
                    <option value="not_subscribed">Mark not subscribed</option>
                    <option value="unsubscribed">Mark unsubscribed</option>
                  </select>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={12} className="p-6 text-center text-white/50">No subscribers match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
